import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Loader, Package, ArrowRight, RefreshCw, ExternalLink, Gift, Share2, Copy, Check } from 'lucide-react';
import api from '../lib/api';
import PageHeader from '../components/PageHeader';
import { useToast } from '../hooks/use-toast';

const STATUS_LABELS = {
  queued: 'Queued',
  in_kitchen: 'In the Kitchen',
  ready: 'Ready for Pickup',
  out_for_delivery: 'Out for Delivery',
  completed: 'Completed',
};

const STATUS_COLORS = {
  queued: 'bg-yellow-50 text-yellow-700',
  in_kitchen: 'bg-blue-50 text-blue-700',
  ready: 'bg-green-50 text-green-700',
  out_for_delivery: 'bg-purple-50 text-purple-700',
  completed: 'bg-gray-100 text-gray-600',
};

export default function MyOrders() {
  const [email, setEmail] = useState('');
  const [orders, setOrders] = useState(null);
  const [loyalty, setLoyalty] = useState(null);
  const [referral, setReferral] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const [ordersRes, loyaltyRes, refRes] = await Promise.all([
        api.post('/orders/lookup', { email }),
        api.post('/loyalty/balance', { email }),
        api.post('/loyalty/referral/info', { email }),
      ]);
      setOrders(ordersRes.data.orders);
      setLoyalty(loyaltyRes.data);
      setReferral(refRes.data);
      if (ordersRes.data.orders.length === 0) {
        toast({ title: 'No orders found', description: 'We couldn\u2019t find any past orders for that email.' });
      }
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'Lookup failed' });
    } finally { setLoading(false); }
  };

  const shareUrl = referral ? `${window.location.origin}/?ref=${referral.code}` : '';

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: 'Could not copy', description: shareUrl });
    }
  };

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Wrappus Donair \u2014 free food coming your way',
          text: `Use my code ${referral.code} on your first Wrappus order and you'll get 25 bonus points!`,
          url: shareUrl,
        });
      } catch { /* user cancelled */ }
    } else {
      copy();
    }
  };

  return (
    <main>
      <PageHeader title="My Orders" breadcrumb="My Orders" />
      <section className="py-14 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-8">
            <p className="text-gray-600">Enter the email you used at checkout to see your recent orders, track their status, or reorder in one tap.</p>
          </div>

          <form onSubmit={submit} className="bg-[#FDF3EC] rounded-3xl p-6 md:p-8 mb-8 flex flex-col md:flex-row gap-3">
            <div className="flex-1 relative">
              <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" className="w-full pl-11 pr-4 py-3 rounded-full border border-gray-200 bg-white focus:outline-none focus:border-[#ED2E2E] focus:ring-2 focus:ring-[#ED2E2E]/20" />
            </div>
            <button type="submit" disabled={loading} className="btn-wrappus text-sm disabled:opacity-60">
              {loading ? <Loader size={16} className="animate-spin" /> : <ArrowRight size={16} />}
              {loading ? 'Looking up...' : 'Find My Orders'}
            </button>
          </form>

          {orders !== null && loyalty && (
            <div className="mb-6 bg-gradient-to-br from-[#ED2E2E] to-[#C81E1E] rounded-3xl p-6 text-white flex items-center gap-5 shadow-lg">
              <div className="w-14 h-14 rounded-2xl bg-white/15 flex items-center justify-center shrink-0">
                <Gift size={26} />
              </div>
              <div className="flex-1">
                <div className="text-xs uppercase tracking-widest opacity-80">Your Rewards Balance</div>
                <div className="text-3xl font-bold">{loyalty.balance} pts</div>
                <div className="text-sm opacity-90 mt-1">
                  {loyalty.balance >= 100
                    ? `Worth up to $${Math.min(50, Math.floor(loyalty.balance / 100) * 10)} off your next order`
                    : `Earn ${100 - loyalty.balance} more points to unlock $10 off`}
                  {loyalty.expiringSoon > 0 && (
                    <span className="block mt-1 text-yellow-200">⏰ {loyalty.expiringSoon} pts expire within 30 days</span>
                  )}
                </div>
              </div>
              <Link to="/menu" className="hidden sm:inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-[#ED2E2E] font-semibold text-sm hover:bg-white/90 transition">Order Now</Link>
            </div>
          )}

          {referral && (
            <div className="mb-6 bg-white border-2 border-[#ED2E2E]/20 rounded-3xl p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-11 h-11 rounded-2xl bg-[#FDF3EC] flex items-center justify-center"><Share2 className="text-[#ED2E2E]" size={20} /></div>
                <div>
                  <h3 className="font-heading font-bold text-[#0F1C2E]">Refer a friend, earn {referral.referralBonus} points</h3>
                  <p className="text-xs text-gray-500">Your friend gets {referral.signupBonus} welcome points too</p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 mt-3">
                <div className="flex-1 flex items-center gap-2 bg-[#FDF3EC] rounded-full px-4 py-2.5">
                  <span className="text-xs text-gray-500 uppercase tracking-widest">Code</span>
                  <span className="font-mono font-bold text-[#0F1C2E] tracking-widest">{referral.code}</span>
                </div>
                <button onClick={copy} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border-2 border-[#ED2E2E] text-[#ED2E2E] font-semibold text-sm hover:bg-[#FDF3EC] transition">
                  {copied ? <Check size={15} /> : <Copy size={15} />} {copied ? 'Copied!' : 'Copy link'}
                </button>
                <button onClick={share} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#ED2E2E] text-white font-semibold text-sm hover:bg-[#C81E1E] transition">
                  <Share2 size={15} /> Share
                </button>
              </div>
              {(referral.successfulReferrals > 0 || referral.pendingReferrals > 0) && (
                <div className="mt-4 text-sm text-gray-600 flex gap-6">
                  <div><strong className="text-[#ED2E2E]">{referral.successfulReferrals}</strong> friends joined</div>
                  {referral.pendingReferrals > 0 && <div><strong className="text-orange-500">{referral.pendingReferrals}</strong> waiting on their first order</div>}
                  <div>You've earned <strong className="text-[#ED2E2E]">{referral.earnedFromReferrals}</strong> pts from referrals</div>
                </div>
              )}
            </div>
          )}

          {orders !== null && (
            orders.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Package size={48} className="mx-auto mb-4 opacity-40" />
                <p>No orders found for <strong>{email}</strong>.</p>
                <Link to="/menu" className="btn-wrappus text-sm mt-4">Browse Menu <ArrowRight size={14} /></Link>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="text-sm text-gray-500 mb-2">Showing {orders.length} recent order{orders.length !== 1 ? 's' : ''}:</div>
                {orders.map((o) => (
                  <div key={o.id} className="bg-white border border-gray-100 rounded-2xl p-6 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div>
                        <div className="text-xs text-gray-500 uppercase tracking-widest mb-1">Order #{o.id.slice(0, 8)}</div>
                        <div className="font-heading font-bold text-[#0F1C2E] text-lg">
                          {o.items?.length || 0} item{o.items?.length !== 1 ? 's' : ''} · ${o.total.toFixed(2)}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {new Date(o.createdAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                          {' · '}
                          <span className="font-semibold">{o.type === 'delivery' ? 'Delivery' : 'Pickup'}</span>
                          {o.scheduledFor && <span className="text-[#ED2E2E]"> · Scheduled {new Date(o.scheduledFor).toLocaleString([], { hour: 'numeric', minute: '2-digit' })}</span>}
                        </div>
                      </div>
                      {o.status === 'paid' ? (
                        <span className={`text-xs px-3 py-1 rounded-full font-semibold whitespace-nowrap ${STATUS_COLORS[o.kitchenStatus] || 'bg-gray-100 text-gray-600'}`}>
                          {STATUS_LABELS[o.kitchenStatus] || o.kitchenStatus}
                        </span>
                      ) : (
                        <span className="text-xs px-3 py-1 rounded-full font-semibold bg-red-50 text-red-700">Payment failed</span>
                      )}
                    </div>
                    <div className="text-sm text-gray-700 mb-4 line-clamp-2">
                      {o.items?.map((i) => `${i.qty}× ${i.name}`).join(', ')}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Link to={`/track/${o.id}`} className="inline-flex items-center gap-1 text-sm px-4 py-2 rounded-full bg-[#FDF3EC] text-[#0F1C2E] font-semibold hover:bg-[#f5d9c8] transition"><RefreshCw size={13} /> Track</Link>
                      <Link to={`/reorder/${o.id}`} className="inline-flex items-center gap-1 text-sm px-4 py-2 rounded-full bg-[#ED2E2E] text-white font-semibold hover:bg-[#C81E1E] transition">Reorder <ArrowRight size={13} /></Link>
                      {o.deliveryTracking && (
                        <a href={o.deliveryTracking} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm px-4 py-2 rounded-full border border-gray-200 text-[#0F1C2E] font-semibold hover:border-[#ED2E2E] transition"><ExternalLink size={13} /> Courier</a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      </section>
    </main>
  );
}
